import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, QrCode, ArrowDownRight } from "lucide-react";
import WhatsAppIcon from "@/components/card/WhatsAppIcon";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ card, onSave, onShare }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Portrait — right, clipped, parallax */}
      <motion.div
        style={{ y, scale }}
        className="absolute right-0 top-0 h-[65vh] w-[80%] md:h-full md:w-[46%]"
      >
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={card.photo}
            alt={card.name}
            className="h-full w-full object-cover object-center grayscale-[35%] contrast-110"
            data-testid="hero-portrait"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
        </div>
      </motion.div>

      {/* Top bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.2 }}
        className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-6 pt-8 md:px-10"
      >
        <span className="flex items-center gap-3">
          {card.logo && (
            <img
              src={card.logo}
              alt={`${card.company} logo`}
              className="h-11 w-11 rounded-lg object-cover ring-1 ring-white/15 md:h-12 md:w-12"
              data-testid="hero-logo"
            />
          )}
          <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-white/70">
            {card.company}
          </span>
        </span>
        <span className="font-mono-accent text-xs uppercase tracking-[0.25em] text-white/40">
          {card.location}
        </span>
      </motion.div>

      {/* Hero copy */}
      <div className="relative z-20 mx-auto flex min-h-[calc(100vh-80px)] max-w-7xl flex-col justify-center px-6 md:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-6 font-mono-accent text-xs uppercase tracking-[0.35em] text-white/50"
        >
          00 // Digital Card
        </motion.p>

        <h1 className="font-display text-[15vw] font-semibold leading-[0.86] tracking-tighter md:text-[9vw] lg:text-[8.5rem]">
          <Line delay={0.1}>{card.firstName}</Line>
          <Line delay={0.25} className="text-stroke">
            {card.lastName}
          </Line>
        </h1>

        <div className="mt-8 max-w-xl overflow-hidden">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease }}
            className="text-lg text-white/70 md:text-xl"
          >
            {card.title}. {card.tagline}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={onSave}
            data-testid="hero-save-contact-btn"
            className="group flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-medium text-[#050505] transition-transform duration-300 hover:scale-[1.03] active:scale-95"
          >
            <Download className="h-4 w-4" />
            Save Contact
          </button>
          <button
            onClick={onShare}
            data-testid="hero-share-btn"
            className="crystal group flex items-center gap-3 rounded-full px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/15"
          >
            <QrCode className="h-4 w-4" />
            Share via QR
          </button>
          {card.whatsapp && (
            <a
              href={`https://wa.me/${card.whatsapp.replace(/[^0-9]/g, "")}`}
              target="_blank"
              rel="noreferrer"
              aria-label="WhatsApp"
              data-testid="hero-whatsapp-btn"
              className="group flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#25D366] text-white transition-transform duration-300 hover:scale-[1.06] active:scale-95"
            >
              <WhatsAppIcon className="h-5 w-5" />
            </a>
          )}
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.1 }}
        className="absolute bottom-8 left-6 z-20 flex items-center gap-2 font-mono-accent text-xs uppercase tracking-widest text-white/40 md:left-10"
      >
        <ArrowDownRight className="h-4 w-4 animate-pulse" />
        Scroll to explore
      </motion.div>
    </section>
  );
}
